/**
 * Organization ranking projections.
 */

import { DEFAULT_POINTS_TABLE } from "./rankingTypes";
import { calculatePointsFromSources, bestPlacementsPerCompetition } from "./pointsEngine";

/**
 * @param {object} input
 * @param {string} input.organizationId
 * @param {string} [input.name]
 * @param {number} [input.memberCount]
 * @param {Array<{ playerId: string, winPct?: number|null, points?: number, placements?: object[] }>} [input.members]
 * @param {Array<{ playerId: string, placement: string, competitionId?: string }>} [input.placements]
 * @param {string[]} [input.organizationCompetitionIds]
 * @param {typeof DEFAULT_POINTS_TABLE} [input.pointsTable]
 */
export function calculateOrganizationRankings(input = {}) {
  const table = input.pointsTable || DEFAULT_POINTS_TABLE;
  const members = input.members || [];
  const placements = bestPlacementsPerCompetition(input.placements || []);

  const titles = placements.filter((p) => p.placement === "winner").length;
  const podiumFinishes = placements.filter((p) =>
    ["winner", "runner_up", "semi"].includes(p.placement)
  ).length;

  // Org points = sum of unique best placements for its competitions + member season points
  const placementPoints = calculatePointsFromSources({
    placements,
    cards: [],
    pointsTable: table,
  }).points;

  const memberPoints = members.reduce((sum, m) => sum + (Number(m.points) || 0), 0);
  const winPctSamples = members
    .map((m) => m.winPct)
    .filter((n) => typeof n === "number" && !Number.isNaN(n));
  const averageWinPct = winPctSamples.length
    ? Math.round(winPctSamples.reduce((a, b) => a + b, 0) / winPctSamples.length)
    : null;

  const totalPoints = placementPoints + Math.round(memberPoints * 0.25);

  return {
    organizationId: input.organizationId,
    name: input.name || "Organization",
    memberCount: input.memberCount ?? members.length,
    tournamentTitles: titles,
    podiumFinishes,
    totalPoints,
    averageWinPct,
    leaderboardPosition: null,
    breakdown: {
      placementPoints,
      memberPointsContribution: Math.round(memberPoints * 0.25),
    },
    algorithmId: table.algorithmId,
  };
}

/**
 * @param {Array<object>} orgRows
 */
export function assignOrganizationRanks(orgRows) {
  const sorted = [...(orgRows || [])].sort(
    (a, b) => (b.totalPoints || 0) - (a.totalPoints || 0)
  );
  sorted.forEach((row, i) => {
    row.leaderboardPosition = i + 1;
  });
  return sorted;
}
